import React, { FC, useMemo } from 'react';

import { Button, Input, RadioButton } from 'application/components';
import { getCurrencyIcon } from 'application/helpers/currency';
import { ErrorOutlineIcon } from 'application/assets';
import { formatTotal } from 'application/helpers/formatTotal';
import { useAppSelector } from 'application/store';

import { CommittedAs } from 'integration/api/Deal/models';

import { InvestmentAmountProps } from './models';
import {
  FormStyled,
  Container,
  AmountFieldSection,
  CurrencyStyled,
  ErrorMessage,
  InvestingAsSection,
  LegalEntityStyled,
  LegalEntityList,
} from './styles';

const InvestAmount: FC<InvestmentAmountProps> = ({
  amount,
  minInvest,
  investmentCurrency,
  legalEntityId,
  isLegalEntity,
  errorMessage,
  register,
  onSubmit,
  handleChangeInvestmentDoneAs,
  handleChangeLegalEntityId,
}) => {
  const { legalEntity } = useAppSelector((state) => state.legalEntity);

  const showLEgalEntityList = !!legalEntity.length && isLegalEntity;
  const legalEntitiesList = useMemo(() => {
    const newEntity = { id: null, name: '(Add a different entity)' };
    return [newEntity, ...legalEntity];
  }, [legalEntity]);

  return (
    <FormStyled onSubmit={onSubmit}>
      <Container>
        {errorMessage && (
          <ErrorMessage>
            <ErrorOutlineIcon />
            <p>
              The minimum investment amount on this deal is{' '}
              <span>{formatTotal(minInvest, investmentCurrency)}</span>. You
              can't commit less of that amount.
            </p>
          </ErrorMessage>
        )}
        <h1>Investment amount</h1>
        <AmountFieldSection>
          <Input
            id="amount"
            label="Enter your amount"
            type="text"
            filled={!!amount}
            register={register}
          />
          <CurrencyStyled>{getCurrencyIcon(investmentCurrency)}</CurrencyStyled>
        </AmountFieldSection>
        <InvestingAsSection>
          <h3>Investing as</h3>
          <RadioButton
            id="individual"
            label="Individual"
            checked={!isLegalEntity}
            onChange={() => {
              handleChangeInvestmentDoneAs(CommittedAs.INDIVIDUAL);
            }}
          />
          <LegalEntityStyled>
            <RadioButton
              id="legal-entity"
              label="Legal entity"
              checked={isLegalEntity}
              onChange={() => {
                handleChangeInvestmentDoneAs(CommittedAs.LEGAL_ENTITY);
              }}
            />
            {showLEgalEntityList && (
              <LegalEntityList>
                <h3>Select legal entity:</h3>
                <div>
                  {legalEntitiesList.map((item) => (
                    <RadioButton
                      key={item.id}
                      id={`legal-entity-${item.id}`}
                      label={item.name}
                      checked={legalEntityId === item.id}
                      onChange={() => {
                        handleChangeLegalEntityId(item.id);
                      }}
                    />
                  ))}
                </div>
              </LegalEntityList>
            )}
          </LegalEntityStyled>
        </InvestingAsSection>
      </Container>
      <Button type="submit">Submit</Button>
    </FormStyled>
  );
};

export default InvestAmount;
